/**
 * Checkout Tracking Hooks
 * 提供统一的埋点方法，简化组件中的埋点调用
 */

import { useCallback } from 'react'

import { useGATracking } from './provider'

import type { GAEventData } from './types'

/**
 * 埋点模块定义
 */
export type TrackingModule =
  | 'checkout_page'
  | 'checkout_header'
  | 'checkout_form'
  | 'customer_info'
  | 'payment_methods'
  | 'order_summary'
  | 'coupon'
  | 'payment_card'
  | 'payment_applepay'
  | 'payment_googlepay'
  | 'payment_resources'
  | 'checkout_footer'
  | 'success_page'
  | 'toast'

/**
 * 埋点动作名称类型
 */
export type TrackingAction =
  // Page View
  | 'page_view_checkout'
  | 'page_view_success'
  // Customer Info
  | 'email_input_focus'
  | 'email_input_blur'
  | 'email_input_valid'
  | 'email_input_invalid'
  // Payment Method Selection
  | 'payment_method_select_card'
  | 'payment_method_select_applepay'
  | 'payment_method_select_googlepay'
  | 'payment_methods_expand'
  | 'payment_methods_collapse'
  // Card Form
  | 'card_number_focus'
  | 'card_number_complete'
  | 'card_number_error'
  | 'card_expiry_complete'
  | 'card_cvc_complete'
  | 'card_form_ready'
  | 'card_form_error'
  // Coupon
  | 'coupon_section_expand'
  | 'coupon_input_focus'
  | 'coupon_apply_click'
  | 'coupon_apply_success'
  | 'coupon_apply_error'
  | 'coupon_remove_click'
  // Order Summary
  | 'order_summary_expand'
  | 'order_summary_collapse'
  | 'currency_selector_open'
  | 'currency_change'
  // Payment Submit
  | 'pay_button_click'
  | 'payment_submit_start'
  | 'payment_submit_success'
  | 'payment_submit_error'
  | 'payment_submit_processing'
  | 'payment_submit_cancelled'
  | 'payment_prepare_start'
  | 'payment_prepare_success'
  | 'payment_prepare_error'
  | 'payment_intent_create_start'
  | 'payment_intent_create_success'
  | 'payment_intent_create_error'
  | 'payment_resource_hint'
  | 'payment_resource_load_start'
  | 'payment_resource_load_retry'
  | 'payment_resource_load_success'
  | 'payment_resource_load_error'
  | 'payment_3ds_start'
  | 'payment_3ds_success'
  | 'payment_3ds_error'
  | 'payment_3ds_cancel'
  // Apple Pay
  | 'applepay_available'
  | 'applepay_unavailable'
  | 'applepay_button_click'
  | 'applepay_sheet_open'
  | 'applepay_submit_success'
  | 'applepay_submit_error'
  | 'applepay_cancel'
  // Google Pay
  | 'googlepay_available'
  | 'googlepay_unavailable'
  | 'googlepay_button_click'
  | 'googlepay_sheet_open'
  | 'googlepay_submit_success'
  | 'googlepay_submit_error'
  | 'googlepay_cancel'
  // Header/Footer
  | 'language_selector_open'
  | 'language_change'
  | 'logo_click'
  | 'link_terms_click'
  | 'link_privacy_click'
  | 'link_refund_click'
  // Success Page
  | 'success_close_click'
  | 'success_continue_click'
  | 'success_download_click'
  // Error & Retry
  | 'error_display'
  | 'error_retry_click'
  | 'timeout_display'
  | 'timeout_retry_click'
  | 'init_error_display'
  | 'init_retry_click'
  // Toast
  | 'toast_show'
  | 'toast_close_click'
  | 'toast_auto_dismiss'

/**
 * 埋点扩展数据
 */
export interface TrackingExtraData {
  /** 支付方式 */
  payment_method?: string
  /** 金额 */
  amount?: number
  /** 货币 */
  currency?: string
  /** 优惠码 */
  coupon_code?: string
  /** 错误信息 */
  error_message?: string
  /** 错误代码 */
  error_code?: string
  /** 语言 */
  language?: string
  /** 链接类型 */
  link_type?: string
  /** 其他自定义数据 */
  [key: string]: unknown
}

/**
 * 统一的 Checkout 埋点 Hook
 *
 * @example
 * ```tsx
 * const { trackEvent, trackPageView, trackPayment } = useCheckoutTracking()
 *
 * // 页面视图
 * useEffect(() => {
 *   trackPageView('checkout')
 * }, [])
 *
 * // 通用事件
 * trackEvent('payment_method_select_card', 'payment_methods')
 *
 * // 支付事件（带额外数据）
 * trackPayment('payment_submit_success', { amount: 99.00 })
 * ```
 */
export function useCheckoutTracking() {
  const { track, isEnabled } = useGATracking()

  /**
   * 发送通用埋点事件
   */
  const trackEvent = useCallback(
    (actionName: TrackingAction, module: TrackingModule, extraData?: TrackingExtraData) => {
      if (!isEnabled) return

      const eventData: GAEventData = {
        event: 'click_interaction',
        action_name: actionName,
        module,
        ...extraData,
      }

      track(eventData)
    },
    [track, isEnabled]
  )

  /**
   * 发送页面视图事件
   */
  const trackPageView = useCallback(
    (page: 'checkout' | 'success', extraData?: TrackingExtraData) => {
      if (!isEnabled) return

      const actionName: TrackingAction =
        page === 'checkout' ? 'page_view_checkout' : 'page_view_success'
      const module: TrackingModule = page === 'checkout' ? 'checkout_page' : 'success_page'

      track({
        event: 'click_interaction',
        action_name: actionName,
        module,
        ...extraData,
      })
    },
    [track, isEnabled]
  )

  /**
   * 发送支付相关事件（自动添加 payment_card 模块）
   */
  const trackPayment = useCallback(
    (actionName: TrackingAction, extraData?: TrackingExtraData) => {
      if (!isEnabled) return

      // 根据 actionName 自动推断 module
      let module: TrackingModule = 'checkout_form'
      if (actionName.startsWith('card_') || actionName.startsWith('payment_3ds')) {
        module = 'payment_card'
      } else if (actionName.startsWith('applepay_')) {
        module = 'payment_applepay'
      } else if (actionName.startsWith('googlepay_')) {
        module = 'payment_googlepay'
      }

      track({
        event: 'click_interaction',
        action_name: actionName,
        module,
        ...extraData,
      })
    },
    [track, isEnabled]
  )

  /**
   * 发送优惠券相关事件
   */
  const trackCoupon = useCallback(
    (actionName: TrackingAction, extraData?: TrackingExtraData) => {
      if (!isEnabled) return

      track({
        event: 'click_interaction',
        action_name: actionName,
        module: 'coupon',
        ...extraData,
      })
    },
    [track, isEnabled]
  )

  /**
   * 发送错误事件
   */
  const trackError = useCallback(
    (
      actionName: TrackingAction,
      errorMessage: string,
      errorCode?: string,
      module: TrackingModule = 'checkout_form'
    ) => {
      if (!isEnabled) return

      track({
        event: 'click_interaction',
        action_name: actionName,
        module,
        error_message: errorMessage,
        error_code: errorCode,
      })
    },
    [track, isEnabled]
  )

  const trackBusinessEvent = useCallback(
    (eventData: GAEventData) => {
      if (!isEnabled) return
      track(eventData)
    },
    [track, isEnabled]
  )

  return {
    trackEvent,
    trackPageView,
    trackPayment,
    trackCoupon,
    trackError,
    trackBusinessEvent,
    isEnabled,
  }
}

/**
 * 生成 data-ga-* 属性的辅助函数
 * 用于自动埋点的元素
 */
export function createGAProps(
  actionName: TrackingAction,
  module: TrackingModule,
  extraData?: TrackingExtraData
) {
  const props: Record<string, string> = {
    'data-ga-action-name': actionName,
    'data-ga-module': module,
  }

  if (extraData) {
    for (const [key, value] of Object.entries(extraData)) {
      if (value !== undefined && value !== null) {
        props[`data-ga-${key.replace(/_/g, '-')}`] = String(value)
      }
    }
  }

  return props
}
